import './style.scss';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import {
  buildAdminPathWithScope,
  normalizeAdminScope
} from '../../utils/adminScope';

export default function BackButton({ fallback = '/', label = 'Volver' }) {
  const navigate = useNavigate();
  const location = useLocation();

  const ROUTE_BACK_MAP = [
    { pattern: '/administrador/semaforo', to: '/administrador' },
    { pattern: '/revisor/calendario', to: '/revisor' },
    {
      pattern: '/revisor/caracterizaciones/ver-beneficiario',
      to: '/revisor/caracterizaciones'
    },
    { pattern: '/revisor/caracterizaciones', to: '/revisor' },
    { pattern: '/calendario', to: '/' },
    { pattern: '/acciones-incidencia/nueva', to: '/acciones-incidencia' },
    { pattern: '/acciones-incidencia/:actionId', to: '/acciones-incidencia' },
    { pattern: '/acciones-incidencia', to: '/escuelas' },
    { pattern: '/exploracion/:schoolId', to: '/registrarEscuelas' },
    { pattern: '/socializacion/:schoolId', to: '/escuelas' },
    {
      pattern: '/escuelas/grupo/lista_de_encuentros/encuentro',
      to: '/escuelas/grupo/lista_de_encuentros'
    },
    {
      pattern: '/escuelas/grupo/lista_de_encuentros/satisfaccion',
      to: '/escuelas/grupo/lista_de_encuentros'
    },
    {
      pattern: '/generar-listado-asistencia',
      to: '/escuelas/grupo/lista_de_encuentros'
    },
    { pattern: '/escuelas/grupo/lista_de_encuentros', to: '/escuelas' },
    { pattern: '/escuelas/asesoria/:schoolParam/evidencias', to: '/escuelas' },
    { pattern: '/escuelas/asesoria/:schoolParam', to: '/escuelas' },
    { pattern: '/escuelas/grupo/:groupParam/school/:schoolParam', to: '/escuelas' },
    { pattern: '/escuelas/grupo', to: '/escuelas' },
    {
      pattern: '/caracterizacion-jornada/school/:schoolParam/group/:groupParam',
      to: '/escuelas/grupo/lista_de_encuentros'
    },
    {
      pattern: '/caracterizacion/:paramValue/school/:schoolParam/group/:groupParam',
      to: params => `/escuelas/grupo/${params.groupParam}/school/${params.schoolParam}`
    },
    { pattern: '/caracterizacion/:paramValue/school/:schoolParam', to: '/escuelas' },
    { pattern: '/caracterizacion/edit/:bid', to: '/escuelas' },
    { pattern: '/caracterizacion/:paramValue', to: '/escuelas' },
    { pattern: '/caracterizacion', to: '/escuelas' },
    { pattern: '/registrarEscuelas', to: '/' },
    { pattern: '/escuelas', to: '/' },
    {
      pattern: '/administrador/menu/escuelas/beneficiarios_inscritos',
      to: '/administrador/menu/escuelas'
    },
    {
      pattern: '/administrador/menu/caracterizacion/ver_beneficiarios',
      to: '/administrador/menu/caracterizacion'
    },
    {
      pattern: '/administrador/menu/asistencias/encuentro',
      to: '/administrador/menu/asistencias'
    },
    {
      pattern: '/administrador/menu/montar-evidencias',
      to: '/administrador/menu'
    },
    {
      pattern: '/administrador/menu/calendario',
      to: '/administrador/menu'
    },
    {
      pattern: '/administrador/avances/analisis-caracterizados',
      to: '/administrador/avances'
    },
    {
      pattern: '/administrador/avances/analisis-cobertura',
      to: '/administrador/avances'
    },
    { pattern: '/administrador/avances', to: '/administrador/menu' },
    { pattern: '/administrador/menu/evidencias', to: '/administrador/menu' },
    { pattern: '/administrador/menu/escuelas', to: '/administrador/menu' },
    { pattern: '/administrador/menu/caracterizacion', to: '/administrador/menu' },
    { pattern: '/administrador/menu/asistencias', to: '/administrador/menu' },
    { pattern: '/administrador/menu', to: '/administrador' },
    {
      pattern: '/administrador/visitas-territorio/visualizar',
      to: '/administrador/visitas-territorio'
    },
    {
      pattern: '/administrador/visitas-territorio/registrar',
      to: '/administrador/visitas-territorio'
    },
    { pattern: '/administrador/visitas-territorio', to: '/administrador' },
    { pattern: '/superadmin/usuarios', to: '/superadmin' },
    { pattern: '/superadmin/plazos-firmas', to: '/superadmin' },
    { pattern: '/superadmin/evidencias-incidencia', to: '/superadmin' },
    { pattern: '/superadmin/bitacoras-cierre', to: '/superadmin' },
    { pattern: '/administrador', to: '/' }
  ];

  function resolveBackRoute(pathname) {
    for (const route of ROUTE_BACK_MAP) {
      const match = matchPath({ path: route.pattern, end: true }, pathname);
      if (!match) continue;
      if (typeof route.to === 'function') return route.to(match.params);
      return route.to;
    }

    if (pathname.startsWith('/administrador')) return '/administrador/menu';
    if (pathname.startsWith('/escuelas') || pathname.startsWith('/caracterizacion')) {
      return '/escuelas';
    }
    return fallback;
  }

  function handleBack() {
    const stateBack = location?.state?.backTo;
    const searchParams = new URLSearchParams(location.search);
    const adminScope = normalizeAdminScope(searchParams.get('scope'));
    const origin = searchParams.get('origin');
    const returnTo = searchParams.get('returnTo');

    if (stateBack) {
      if (typeof stateBack === 'string') {
        navigate(stateBack, { replace: true });
        return;
      }

      if (typeof stateBack === 'object' && stateBack.pathname) {
        navigate(stateBack.pathname, {
          state: stateBack.state,
          replace: true
        });
        return;
      }
    }

    if (returnTo) {
      navigate(returnTo, { replace: true });
      return;
    }

    if (origin === 'admin-evidences') {
      navigate(
        buildAdminPathWithScope('/administrador/menu/evidencias', adminScope),
        { replace: true }
      );
      return;
    }

    if (
      adminScope === 'general' &&
      (
        location.pathname === '/administrador/avances' ||
        location.pathname === '/administrador/menu/calendario'
      )
    ) {
      navigate(
        buildAdminPathWithScope('/administrador', adminScope),
        { replace: true }
      );
      return;
    }

    const targetRoute = resolveBackRoute(location.pathname);
    const scopedTargetRoute =
      typeof targetRoute === 'string' && targetRoute.startsWith('/administrador')
        ? buildAdminPathWithScope(targetRoute, adminScope)
        : targetRoute;

    if (scopedTargetRoute === location.pathname) {
      navigate(fallback, { replace: true });
      return;
    }
    navigate(scopedTargetRoute, { replace: true });
  }

  return (
    <button
      type='button'
      onClick={handleBack}
      className='back-button back--button'
      aria-label='Volver'>
      <span className='material-symbols-rounded back__icon' aria-hidden='true'>
        arrow_back
      </span>
      <span className='back__text'>{label}</span>
    </button>
  );
}
